// src/components/workspace/ViewOptions.tsx
// Everything that narrows or arranges a list, as sections of a settings
// surface: search, the view it is drawn as, filters, grouping, sorting,
// density, and the way back to none of it.
//
// These used to be three rows of always-visible controls above the data, and
// then a row of controls in the pane's tab strip. They are sections now because
// they are all edits — you set them once and read the result. What survives on
// screen is the *state* they produced: the active filters stay as chips in the
// navbar (see FilterChips), because a filter you cannot see is a short list
// with no explanation.
//
// Nothing here opens a popover of its own; the surface decides whether a
// section is a drill-down row or a heading with its list under it. The one
// exception is the assignee filter, which borrows the app's user picker and
// says so (`hostsPopover`).

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import { DensityToggle } from '@/components/DensityToggle';
import { UserPicker } from '@/components/UserPicker';
import { useUserNames } from '@/features/workspace/useUserNames';
import {
  SettingsLabel,
  SettingsRootOnly,
  SettingsSection,
  settingsRowClass,
  useSettingsSurface,
} from '@/components/workspace/SettingsSurface';
import { useWorkspace } from '@/features/workspace/context';
import { getAvailableGroups } from '@/features/workspace/logic';

import type {
  DensityMode,
  FilterConfig,
  FilterValue,
  SortDirection,
  ViewType,
} from '@/features/workspace/types';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const VIEW_ICONS: Record<string, string> = {
  list: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  board: 'M9 4h6v16H9zM3 4h6v10H3zM15 4h6v12h-6z',
  timeline: 'M3 12h4l3-9 4 18 3-9h4',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  schedule: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  tree: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  grid: 'M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z',
  table: 'M3 10h18M3 14h18M3 18h18M3 6h18',
};

function ViewIcon({ icon, className }: { icon: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={VIEW_ICONS[icon] || VIEW_ICONS.list} />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function ArrowUpDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3v18" />
      <path d="M3 7l4-4 4 4" />
      <path d="M17 21V3" />
      <path d="M21 17l-4 4-4-4" />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Value readers — pull the current value of a field out of the active filters
// ---------------------------------------------------------------------------

function arrayValue(filters: FilterValue[], field: string): string[] {
  const existing = filters.find((f) => f.field === field);
  if (!existing) return [];
  return Array.isArray(existing.value) ? (existing.value as string[]) : [String(existing.value)];
}

function rangeValue(filters: FilterValue[], field: string): [string, string] {
  const existing = filters.find((f) => f.field === field);
  if (existing && existing.operator === 'between' && Array.isArray(existing.value)) {
    const [from, to] = existing.value as [string, string];
    return [from ?? '', to ?? ''];
  }
  return ['', ''];
}

// ---------------------------------------------------------------------------
// Inline editors for filter kinds that have no fixed option list
// ---------------------------------------------------------------------------

interface DateRangeEditorProps {
  initial: [string, string];
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}

function DateRangeEditor({ initial, onApply, onClear }: DateRangeEditorProps) {
  const { t } = useI18n();
  const [from, setFrom] = useState(initial[0]);
  const [to, setTo] = useState(initial[1]);

  return (
    <div className="p-2 space-y-2">
      <label className="block text-xs font-medium text-muted-foreground">
        {t('workspace.filter.dateFrom')}
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        {t('workspace.filter.dateTo')}
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onApply(from, to)}
          className="flex-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t('workspace.filter.apply')}
        </button>
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('common.clear')}
        </button>
      </div>
    </div>
  );
}

interface TextValueEditorProps {
  values: string[];
  placeholder: string;
  onAdd: (raw: string) => void;
  onClear: () => void;
}

function TextValueEditor({ values, placeholder, onAdd, onClear }: TextValueEditorProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  return (
    <div className="p-2 space-y-2">
      <input
        type="text"
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdd(draft);
            setDraft('');
          }
        }}
        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {values.length > 0 && (
        <p className="text-xs text-muted-foreground truncate">{values.join(', ')}</p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => { onAdd(draft); setDraft(''); }}
          className="flex-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t('workspace.filter.addValue')}
        </button>
        {values.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('common.clear')}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-value people filter: picker + the chosen people as removable chips
// ---------------------------------------------------------------------------

interface UserFilterBodyProps {
  cfg: FilterConfig;
  values: string[];
  onSelect: (cfg: FilterConfig, userId: string) => void;
}

const UserFilterBody = observer(({ cfg, values, onSelect }: UserFilterBodyProps) => {
  const { t } = useI18n();
  const names = useUserNames(values);

  return (
    <div className="space-y-2 p-2">
      <UserPicker
        value=""
        onChange={(userId) => onSelect(cfg, userId)}
        placeholder={t('workspace.filter.selectUser')}
      />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs text-foreground"
            >
              {names[id] || `${id.slice(0, 8)}…`}
              <button
                type="button"
                onClick={() => onSelect(cfg, id)}
                aria-label={t('workspace.filter.removeValue')}
                className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** How many things are narrowing the list right now — the menu button's badge. */
export function activeNarrowingCount(filterCount: number, search: string): number {
  return filterCount + (search.trim() ? 1 : 0);
}

export const ViewOptions = observer(() => {
  const { t } = useI18n();
  const { state, config, dispatch, searchDraft, onSearchDraftChange } = useWorkspace();
  const { dismiss } = useSettingsSurface();

  const availableGroups = getAvailableGroups(config, state.view);
  const views = config.availableViews.filter((v) => v.available);
  const activeCount = activeNarrowingCount(state.filters.length, searchDraft);

  // ---- Filter writes ----

  const addFilter = (filter: FilterValue) => dispatch({ type: 'ADD_FILTER', filter });
  const removeFilter = (field: string) => dispatch({ type: 'REMOVE_FILTER', field });

  const handleOptionSelect = (cfg: FilterConfig, optionValue: string) => {
    const existing = state.filters.find((f) => f.field === cfg.field);
    if (existing && existing.operator === 'in' && Array.isArray(existing.value)) {
      const values = existing.value as string[];
      const next = values.includes(optionValue)
        ? values.filter((v) => v !== optionValue)
        : [...values, optionValue];
      if (next.length === 0) removeFilter(cfg.field);
      else addFilter({ field: cfg.field, operator: 'in', value: next, labelKey: cfg.labelKey });
    } else {
      addFilter({ field: cfg.field, operator: 'in', value: [optionValue], labelKey: cfg.labelKey });
    }
  };

  // People filters accumulate: picking a second person widens the filter
  // instead of replacing the first. Picking the same person again removes them.
  const handleUserSelect = (cfg: FilterConfig, userId: string) => {
    if (!userId) return;
    const values = arrayValue(state.filters, cfg.field);
    const next = values.includes(userId)
      ? values.filter((v) => v !== userId)
      : [...values, userId];
    if (next.length === 0) removeFilter(cfg.field);
    else addFilter({ field: cfg.field, operator: 'in', value: next, labelKey: cfg.labelKey });
  };

  const handleDateRangeApply = (cfg: FilterConfig, from: string, to: string) => {
    if (!from && !to) removeFilter(cfg.field);
    else {
      addFilter({
        field: cfg.field,
        operator: 'between',
        value: [from, to] as [string, string],
        labelKey: cfg.labelKey,
      });
    }
    dismiss();
  };

  const handleTextAdd = (cfg: FilterConfig, raw: string) => {
    const next = raw.trim();
    if (!next) return;
    const existing = state.filters.find((f) => f.field === cfg.field);
    const values = existing && Array.isArray(existing.value) ? (existing.value as string[]) : [];
    if (values.includes(next)) return;
    addFilter({ field: cfg.field, operator: 'in', value: [...values, next], labelKey: cfg.labelKey });
  };

  const isOptionSelected = (cfg: FilterConfig, optionValue: string): boolean => {
    const existing = state.filters.find((f) => f.field === cfg.field);
    if (!existing) return false;
    if (Array.isArray(existing.value)) return (existing.value as string[]).includes(optionValue);
    return existing.value === optionValue;
  };

  // ---- Summaries shown on the rows ----

  const activeFields = new Set(state.filters.map((f) => f.field));

  const activeView = views.find((v) => v.id === state.view) ?? views[0];
  const groupLabel = state.groupBy
    ? availableGroups.find((g) => g.field === state.groupBy)?.labelKey
    : null;
  const sortLabel = state.sortBy
    ? config.availableSorts.find((s) => s.field === state.sortBy)?.labelKey
    : null;
  const DirectionIcon = state.sortDirection === 'asc' ? ArrowUpIcon : ArrowDownIcon;

  const filterBody = (cfg: FilterConfig) => {
    if (cfg.options && cfg.options.length > 0) {
      return cfg.options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="menuitem"
          className={cn(settingsRowClass, 'text-foreground hover:bg-accent/50')}
          onClick={() => handleOptionSelect(cfg, option.value)}
        >
          <span
            className={cn(
              'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
              isOptionSelected(cfg, option.value)
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border',
            )}
          >
            {isOptionSelected(cfg, option.value) && <CheckIcon className="h-3 w-3" />}
          </span>
          <span className="flex-1 truncate">{t(option.labelKey)}</span>
        </button>
      ));
    }
    if (cfg.type === 'user') {
      return (
        <UserFilterBody
          cfg={cfg}
          values={arrayValue(state.filters, cfg.field)}
          onSelect={handleUserSelect}
        />
      );
    }
    if (cfg.type === 'date-range' || cfg.type === 'date') {
      return (
        <DateRangeEditor
          initial={rangeValue(state.filters, cfg.field)}
          onApply={(from, to) => handleDateRangeApply(cfg, from, to)}
          onClear={() => { removeFilter(cfg.field); dismiss(); }}
        />
      );
    }
    return (
      <TextValueEditor
        values={arrayValue(state.filters, cfg.field)}
        placeholder={t('workspace.filter.valuePlaceholder')}
        onAdd={(raw) => handleTextAdd(cfg, raw)}
        onClear={() => { removeFilter(cfg.field); dismiss(); }}
      />
    );
  };

  return (
    <>
      {/* Search lives in the navbar next to the view switcher; the menu keeps
          only the edits you make once. */}
      {views.length > 1 && (
        <SettingsSection
          id="view"
          label={t('workspace.views.label')}
          summary={activeView ? t(activeView.labelKey) : undefined}
          icon={<ViewIcon icon={activeView?.icon ?? 'list'} className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
        >
          {views.map((view) => (
            <button
              key={view.id}
              type="button"
              role="menuitem"
              onClick={() => dispatch({ type: 'SET_VIEW', view: view.id as ViewType })}
              className={cn(
                settingsRowClass,
                state.view === view.id
                  ? 'bg-accent text-accent-foreground'
                  : 'text-foreground hover:bg-accent/50',
              )}
            >
              <ViewIcon icon={view.icon} className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{t(view.labelKey)}</span>
              {state.view === view.id && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
            </button>
          ))}
        </SettingsSection>
      )}

      {config.availableFilters.length > 0 && (
        <SettingsRootOnly>
          <SettingsLabel>{t('workspace.filter.sectionLabel')}</SettingsLabel>
        </SettingsRootOnly>
      )}
      {config.availableFilters.map((cfg) => (
        <SettingsSection
          key={cfg.field}
          id={`filter:${cfg.field}`}
          label={t(cfg.labelKey)}
          icon={<FilterIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
          active={activeFields.has(cfg.field)}
          hostsPopover={cfg.type === 'user' && !(cfg.options && cfg.options.length > 0)}
        >
          {filterBody(cfg)}
        </SettingsSection>
      ))}

      {(availableGroups.length > 0 || config.availableSorts.length > 0) && (
        <SettingsRootOnly>
          <SettingsLabel>{t('workspace.arrangeLabel')}</SettingsLabel>
        </SettingsRootOnly>
      )}

      {availableGroups.length > 0 && (
        <SettingsSection
          id="group"
          label={t('workspace.group.label')}
          summary={groupLabel ? t(groupLabel) : t('workspace.group.none')}
          icon={<LayersIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
        >
          <button
            type="button"
            role="menuitem"
            className={cn(settingsRowClass, state.groupBy === null ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50')}
            onClick={() => dispatch({ type: 'SET_GROUP_BY', groupBy: null })}
          >
            <span className="flex-1">{t('workspace.group.none')}</span>
            {state.groupBy === null && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
          </button>
          {availableGroups
            .filter((group) => group.field !== 'none')
            .map((group) => {
              const isActive = state.groupBy === group.field;
              return (
                <button
                  key={group.field}
                  type="button"
                  role="menuitem"
                  className={cn(settingsRowClass, isActive ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50')}
                  onClick={() => dispatch({ type: 'SET_GROUP_BY', groupBy: group.field })}
                >
                  <span className="flex-1 truncate">{t(group.labelKey)}</span>
                  {isActive && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
                </button>
              );
            })}
        </SettingsSection>
      )}

      {config.availableSorts.length > 0 && (
        <SettingsSection
          id="sort"
          label={t('workspace.sort.label')}
          summary={sortLabel ? t(sortLabel) : '—'}
          icon={<ArrowUpDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
        >
          {config.availableSorts.map((sort) => {
            const isActive = state.sortBy === sort.field;
            return (
              <button
                key={sort.field}
                type="button"
                role="menuitem"
                className={cn(settingsRowClass, isActive ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50')}
                onClick={() => {
                  // Picking the field you are already sorted by flips the
                  // direction — the second thing you want from a sort control.
                  const direction: SortDirection | undefined = isActive
                    ? state.sortDirection === 'asc' ? 'desc' : 'asc'
                    : undefined;
                  dispatch({ type: 'SET_SORT_BY', field: sort.field, direction });
                }}
              >
                <span className="flex-1 truncate">{t(sort.labelKey)}</span>
                {isActive && <DirectionIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
              </button>
            );
          })}
        </SettingsSection>
      )}

      <SettingsRootOnly>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <span className="text-sm text-foreground">{t('workspace.density.label')}</span>
          <DensityToggle
            density={state.density}
            onDensityChange={(density: DensityMode) => dispatch({ type: 'SET_DENSITY', density })}
          />
        </div>

        {activeCount > 0 && (
          <>
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              role="menuitem"
              className={cn(settingsRowClass, 'text-muted-foreground hover:bg-accent/50 hover:text-foreground')}
              onClick={() => {
                onSearchDraftChange('');
                dispatch({ type: 'CLEAR_FILTERS' });
                dismiss();
              }}
            >
              <XIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('workspace.filter.clearAll')}</span>
            </button>
          </>
        )}
      </SettingsRootOnly>
    </>
  );
});

ViewOptions.displayName = 'ViewOptions';
